import DescriptionIcon from "@mui/icons-material/Description";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Alert,
  Box,
  Button,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  IconButton,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import { Upload } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ExpertVerification,
  ExpertVerificationStatus,
} from "../../../../../models/user";
import { ExpertVerificationService } from "../../../../../services/ExpertVerificationService";
import { CardCustom } from "../../../../Reusable/CardCustom";

const ACCEPTED = ".pdf,.png,.jpg,.jpeg,.webp,.heic";

const statusColor = (
  status: ExpertVerificationStatus
): "default" | "warning" | "success" | "error" => {
  switch (status) {
    case "VERIFIED":
      return "success";
    case "PENDING":
      return "warning";
    case "REJECTED":
    case "SUSPENDED":
      return "error";
    default:
      return "default";
  }
};

/**
 * Carte de vérification du profil expert : consultation du statut, motif de
 * refus le cas échéant, et soumission / re-soumission des justificatifs.
 */
const ExpertVerificationCard: React.FC = () => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [verification, setVerification] = useState<ExpertVerification | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  const load = () => {
    setLoading(true);
    ExpertVerificationService.getMyVerification()
      .then((res) => setVerification(res))
      .catch(() => setVerification(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const onFilesSelected = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setSubmitting(true);
    setFeedback(null);
    ExpertVerificationService.submitDocuments(Array.from(files))
      .then((res) => {
        setVerification(res);
        setFeedback({
          type: "success",
          message: t("EXPERT_VERIFICATION_SUBMIT_SUCCESS"),
        });
      })
      .catch(() =>
        setFeedback({
          type: "error",
          message: t("EXPERT_VERIFICATION_SUBMIT_ERROR"),
        })
      )
      .finally(() => {
        setSubmitting(false);
        if (fileInputRef.current) fileInputRef.current.value = "";
      });
  };

  const previewDocument = async (documentId: string) => {
    try {
      const url = await ExpertVerificationService.getDocumentObjectUrl(
        documentId
      );
      window.open(url, "_blank");
    } catch {
      setFeedback({
        type: "error",
        message: t("EXPERT_VERIFICATION_DOCUMENT_ERROR"),
      });
    }
  };

  const status = verification?.status ?? "NOT_SUBMITTED";

  return (
    <CardCustom>
      <CardHeader
        title={t("EXPERT_VERIFICATION_SECTION_TITLE")}
        subheader={t("EXPERT_VERIFICATION_SECTION_SUB")}
        sx={{
          "& .MuiCardHeader-title": {
            color: "hsl(var(--foreground))",
            fontWeight: 600,
          },
          "& .MuiCardHeader-subheader": {
            color: "hsl(var(--muted-foreground))",
          },
        }}
      />
      <CardContent
        sx={{ bgcolor: "hsl(var(--card))", color: "hsl(var(--foreground))" }}
      >
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <Typography variant="body2">
                {t("EXPERT_VERIFICATION_CURRENT_STATUS")}:
              </Typography>
              <Chip
                size="small"
                label={t(`EXPERT_VERIFICATION_STATUS_${status}`)}
                color={statusColor(status)}
              />
            </Box>

            {status === "REJECTED" && verification?.reason && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {t("EXPERT_VERIFICATION_REJECTED_REASON")}: {verification.reason}
              </Alert>
            )}
            {status === "SUSPENDED" && verification?.reason && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {t("EXPERT_VERIFICATION_SUSPENDED_REASON")}: {verification.reason}
              </Alert>
            )}
            {status === "PENDING" && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {t("EXPERT_VERIFICATION_PENDING_INFO")}
              </Alert>
            )}
            {status === "VERIFIED" && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {t("EXPERT_VERIFICATION_VERIFIED_INFO")}
              </Alert>
            )}

            {verification?.documents && verification.documents.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {t("EXPERT_VERIFICATION_MY_DOCUMENTS")}
                </Typography>
                {verification.documents.map((doc) => (
                  <Box
                    key={doc.id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "6px",
                      p: 1,
                      mb: 1,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <DescriptionIcon fontSize="small" />
                      <Box>
                        <Typography variant="body2">{doc.fileName}</Typography>
                        {doc.uploadedAt && (
                          <Typography
                            variant="caption"
                            sx={{ color: "hsl(var(--muted-foreground))" }}
                          >
                            {dayjs(doc.uploadedAt).format("DD/MM/YYYY")}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    <IconButton
                      size="small"
                      title={t("EXPERT_VERIFICATION_PREVIEW")}
                      onClick={() => previewDocument(doc.id)}
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}

            {feedback && (
              <Alert severity={feedback.type} sx={{ mb: 2 }}>
                {feedback.message}
              </Alert>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED}
              multiple
              hidden
              onChange={(e) => onFilesSelected(e.target.files)}
            />
            <Button
              variant="outlined"
              startIcon={
                submitting ? <CircularProgress size={16} /> : <Upload size={16} />
              }
              disabled={submitting}
              onClick={() => fileInputRef.current?.click()}
              sx={{
                borderColor: "hsl(var(--border))",
                color: "hsl(var(--foreground))",
                "&:hover": {
                  bgcolor: "hsl(var(--muted))",
                  borderColor: "hsl(var(--border))",
                },
              }}
            >
              {status === "NOT_SUBMITTED"
                ? t("EXPERT_VERIFICATION_SUBMIT_DOCUMENTS")
                : t("EXPERT_VERIFICATION_RESUBMIT_DOCUMENTS")}
            </Button>
            <Typography
              variant="caption"
              sx={{
                display: "block",
                mt: 1,
                color: "hsl(var(--muted-foreground))",
              }}
            >
              {t("EXPERT_VERIFICATION_UPLOAD_HINT")}
            </Typography>
          </>
        )}
      </CardContent>
    </CardCustom>
  );
};

export default ExpertVerificationCard;
